import React from 'react';

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(amount, currency) {
  if (!amount) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
}

function StatusBadge({ name, color }) {
  return (
    <span className="status-badge" style={{ background: `${color}18`, color, borderColor: `${color}40` }}>
      <span className="status-dot" style={{ background: color }} />
      {name}
    </span>
  );
}

function SortHeader({ label, sortKey, sortConfig, onSort }) {
  const isActive = sortConfig.key === sortKey;
  return (
    <th className={`sortable ${isActive ? 'sorted' : ''}`} onClick={() => onSort(sortKey)}>
      {label}
      <span className="sort-arrow">
        {isActive ? (sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193') : ''}
      </span>
    </th>
  );
}

export default function OrderTable({
  orders, activeTypes, sortConfig, onSort,
  designers, onAssign, onViewHistory,
  currentPage, totalPages, onPageChange, totalOrders,
}) {
  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className="order-table">
          <thead>
            <tr>
              <SortHeader label="#" sortKey="id" sortConfig={sortConfig} onSort={onSort} />
              <SortHeader label="Scan Center" sortKey="scanCenter" sortConfig={sortConfig} onSort={onSort} />
              <SortHeader label="Doctor" sortKey="doctor" sortConfig={sortConfig} onSort={onSort} />
              <SortHeader label="Patient" sortKey="patientName" sortConfig={sortConfig} onSort={onSort} />
              <th>Lock</th>
              <th>Service</th>
              <th>Status</th>
              <th>Arch</th>
              <th>Amount</th>
              <th>Received</th>
              <th>Updated</th>
              <th>Assigned To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={13} className="empty-state">
                  <div className="empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p>No orders match your current filters</p>
                  <span>Try adjusting your filter criteria</span>
                </td>
              </tr>
            ) : (
              orders.map(order => (
                <React.Fragment key={order.id}>
                  {order.subOrders.filter(sub => activeTypes[sub.typeCode]).map((sub, idx) => (
                    <tr
                      key={sub.id}
                      className={`
                        ${idx === 0 ? 'block-start' : ''}
                        ${idx === order.subOrders.filter(s => activeTypes[s.typeCode]).length - 1 ? 'block-end' : ''}
                        ${order.isRush ? 'rush-row' : ''}
                        ${order.isThirdParty ? (order.thirdPartySource === 'EXOCAD' ? 'exocad-row' : 'third-party-row') : ''}
                      `}
                    >
                      {idx === 0 ? (
                        <>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-id">
                            <span className="order-id">{order.id}</span>
                            {order.isRush && <span className="rush-badge">RUSH</span>}
                            {order.potentiallyOldOrder && <span className="old-badge">OLD</span>}
                            {order.isThirdParty && <span className="tp-badge">{order.thirdPartySource}</span>}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-scan-center">
                            {order.scanCenter}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-doctor">
                            {order.doctor}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-patient">
                            {order.patientName}
                            {order.reviewedBefore && (
                              <span className="reviewed-link" title={`Previously: #${order.reviewedBefore.join(', #')}`}>
                                prev
                              </span>
                            )}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-lock">
                            {order.isLocked ? (
                              <span className="lock-indicator locked" title={`Locked by ${order.lockedBy}`}>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z"/></svg>
                              </span>
                            ) : (
                              <span className="lock-indicator unlocked">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" opacity="0.2"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h2c0-1.66 1.34-3 3-3s3 1.34 3 3v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/></svg>
                              </span>
                            )}
                          </td>
                        </>
                      ) : null}
                      <td>
                        <span className="service-tag" style={{ background: `${sub.statusColor}18`, color: sub.statusColor }}>
                          {sub.type}
                        </span>
                      </td>
                      <td>
                        <StatusBadge name={sub.statusName} color={sub.statusColor} />
                      </td>
                      {idx === 0 ? (
                        <>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-arch">
                            <div className="arch-info">
                              {order.maxilla !== '-' && <span className="arch-tag max">Mx: {order.maxilla}</span>}
                              {order.mandible !== '-' && <span className="arch-tag mand">Mn: {order.mandible}</span>}
                              {order.maxilla === '-' && order.mandible === '-' && <span className="muted">-</span>}
                            </div>
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-amount">
                            {formatCurrency(order.amountBilled, order.currency)}
                            {order.voucher && <span className="voucher-tag">{order.voucher}</span>}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-date">
                            {formatDate(order.receivedTime)}
                          </td>
                          <td rowSpan={order.subOrders.filter(s => activeTypes[s.typeCode]).length} className="cell-date">
                            {formatDate(order.updateTime)}
                          </td>
                        </>
                      ) : null}
                      <td className="cell-assigned">
                        {sub.assignedToName ? (
                          <span className="assigned-name">{sub.assignedToName}</span>
                        ) : (
                          <span className="unassigned">Unassigned</span>
                        )}
                      </td>
                      <td className="cell-actions">
                        <button className="btn-icon" title="Assign" onClick={() => onAssign(order.id, sub.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
                          </svg>
                        </button>
                        <button className="btn-icon" title="History" onClick={() => onViewHistory(order.id, sub.serviceId)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <span className="pagination-info">
          Showing {Math.min((currentPage - 1) * 50 + 1, totalOrders)}-{Math.min(currentPage * 50, totalOrders)} of {totalOrders}
        </span>
        <div className="pagination-controls">
          <button
            className="btn-page"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M15 19l-7-7 7-7" /></svg>
            Previous
          </button>
          <span className="page-number">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn-page"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
